<template>
  <div class="supplier-delivery-stats">
    <!-- Header Card -->
    <b-card class="mb-3 header-card">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h4 class="mb-0">
            <feather-icon
              icon="PackageIcon"
              size="24"
              class="mr-1"
            />
            Statistiques des Livraisons par Fournisseur
          </h4>
          <small class="text-muted">Analyse des commandes livrées groupées par fournisseur</small>
        </div>
      </div>
    </b-card>

    <!-- Date Filter Card -->
    <b-card class="mb-3">
      <b-row>
        <b-col md="4">
          <b-form-group
            label="Date de début"
            label-for="start-date"
          >
            <b-form-input
              id="start-date"
              v-model="filters.startDate"
              type="date"
              :max="filters.endDate"
            />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group
            label="Date de fin"
            label-for="end-date"
          >
            <b-form-input
              id="end-date"
              v-model="filters.endDate"
              type="date"
              :min="filters.startDate"
              :max="today"
            />
          </b-form-group>
        </b-col>
        <b-col
          md="4"
          class="d-flex align-items-end"
        >
          <b-button
            variant="primary"
            class="mb-1 mr-2"
            @click="fetchStats"
          >
            <feather-icon
              icon="SearchIcon"
              size="16"
              class="mr-50"
            />
            Rechercher
          </b-button>
          <b-button
            variant="outline-secondary"
            class="mb-1"
            @click="resetFilters"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="16"
              class="mr-50"
            />
            Réinitialiser
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <!-- Loading State -->
    <div
      v-if="isLoading"
      class="text-center py-5"
    >
      <b-spinner
        variant="primary"
        style="width: 3rem; height: 3rem;"
      />
      <p class="mt-2 text-muted">
        Chargement des données...
      </p>
    </div>

    <!-- Summary Cards -->
    <template v-else-if="summary">
      <b-row class="match-height mb-3">
        <b-col
          lg="4"
          md="6"
          sm="12"
        >
          <b-card class="stat-card">
            <div class="d-flex justify-content-between">
              <div>
                <p class="stat-label">
                  Nombre de Fournisseurs
                </p>
                <h3 class="stat-value text-primary">
                  {{ summary.total_suppliers }}
                </h3>
              </div>
              <div class="stat-icon bg-light-primary">
                <feather-icon
                  icon="UsersIcon"
                  size="24"
                />
              </div>
            </div>
          </b-card>
        </b-col>

        <b-col
          lg="3"
          md="6"
          sm="12"
        >
          <b-card class="stat-card">
            <div class="d-flex justify-content-between">
              <div>
                <p class="stat-label">
                  Commandes Livré Payé
                </p>
                <h3 class="stat-value text-success">
                  {{ summary.total_livre_paye_orders }}
                </h3>
              </div>
              <div class="stat-icon bg-light-success">
                <feather-icon
                  icon="CheckCircleIcon"
                  size="24"
                />
              </div>
            </div>
          </b-card>
        </b-col>

        <b-col
          lg="4"
          md="6"
          sm="12"
        >
          <b-card class="stat-card">
            <div class="d-flex justify-content-between">
              <div>
                <p class="stat-label">
                  Total Frais de Livraison
                </p>
                <h3 class="stat-value text-warning">
                  {{ formatCurrency(summary.total_delivery_fees) }}
                </h3>
              </div>
              <div class="stat-icon bg-light-warning">
                <feather-icon
                  icon="TruckIcon"
                  size="24"
                />
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Suppliers Table -->
      <b-card
        v-if="suppliers.length > 0"
        title="Détails par Fournisseur"
      >
        <b-row class="mb-2">
          <b-col md="6">
            <b-form-input
              v-model="searchTerm"
              placeholder="Rechercher un fournisseur..."
              type="text"
            >
              <template #prepend>
                <feather-icon icon="SearchIcon" />
              </template>
            </b-form-input>
          </b-col>
          <b-col
            md="6"
            class="text-right"
          >
            <b-button
              variant="outline-success"
              size="sm"
              @click="exportToCSV"
            >
              <feather-icon
                icon="DownloadIcon"
                size="16"
                class="mr-50"
              />
              Exporter CSV
            </b-button>
          </b-col>
        </b-row>

        <b-table
          :items="filteredSuppliers"
          :fields="tableFields"
          :per-page="perPage"
          :current-page="currentPage"
          striped
          hover
          responsive
          show-empty
          empty-text="Aucune donnée disponible"
        >
          <template #cell(supplier_name)="data">
            <div class="d-flex align-items-center">
              <b-avatar
                :text="getInitials(data.item.supplier_name)"
                variant="light-primary"
                size="32"
                class="mr-1"
              />
              <span class="font-weight-bold">{{ data.item.supplier_name }}</span>
            </div>
          </template>

          <template #cell(orders_livre_paye_count)="data">
            <b-badge
              variant="light-success"
              pill
              class="font-weight-bold"
            >
              {{ data.item.orders_livre_paye_count }}
            </b-badge>
          </template>

          <template #cell(invoice_ids)="data">
            <div class="d-flex flex-wrap gap-1">
              <b-badge
                v-for="invoiceId in data.item.invoice_ids"
                :key="invoiceId"
                variant="light-info"
                class="mr-1 mb-1"
              >
                #{{ invoiceId }}
              </b-badge>
            </div>
          </template>

          <template #cell(total_delivery_fees)="data">
            <span class="font-weight-bold text-warning">
              {{ formatCurrency(data.item.total_delivery_fees) }}
            </span>
          </template>

          <template #cell(avg_delivery_fee)="data">
            <span class="text-muted">
              {{ formatCurrency(data.item.orders_livre_paye_count > 0 ? data.item.total_delivery_fees / data.item.orders_livre_paye_count : 0) }}
            </span>
          </template>
        </b-table>

        <b-pagination
          v-model="currentPage"
          :total-rows="filteredSuppliers.length"
          :per-page="perPage"
          align="center"
          class="mt-2"
        />
      </b-card>

      <!-- Empty State -->
      <b-card v-else>
        <div class="text-center py-5">
          <feather-icon
            icon="InboxIcon"
            size="48"
            class="text-muted mb-2"
          />
          <h5 class="text-muted">
            Aucune donnée disponible pour cette période
          </h5>
          <p class="text-muted">
            Essayez de sélectionner une autre plage de dates
          </p>
        </div>
      </b-card>
    </template>

    <!-- Error State -->
    <b-card v-else-if="error">
      <b-alert
        variant="danger"
        show
      >
        <h4 class="alert-heading">
          Erreur
        </h4>
        <p>{{ error }}</p>
      </b-alert>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BButton,
  BSpinner,
  BTable,
  BBadge,
  BAvatar,
  BPagination,
  BAlert,
} from 'bootstrap-vue'
import axios from 'axios'

export default {
  name: 'SupplierDeliveryStats',
  components: {
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BButton,
    BSpinner,
    BTable,
    BBadge,
    BAvatar,
    BPagination,
    BAlert,
  },
  data() {
    return {
      isLoading: false,
      error: null,
      filters: {
        startDate: this.getDefaultStartDate(),
        endDate: this.getDefaultEndDate(),
      },
      suppliers: [],
      summary: null,
      searchTerm: '',
      currentPage: 1,
      perPage: 10,
      tableFields: [
        {
          key: 'supplier_name',
          label: 'Fournisseur',
          sortable: true,
        },
        {
          key: 'orders_livre_paye_count',
          label: 'Commandes Livré Payé',
          sortable: true,
          tdClass: 'text-center',
        },
        {
          key: 'invoice_ids',
          label: 'Règlements',
          sortable: false,
        },
        {
          key: 'total_delivery_fees',
          label: 'Frais de Livraison',
          sortable: true,
        },
        {
          key: 'avg_delivery_fee',
          label: 'Frais Moyen par Commande',
          sortable: true,
        },
      ],
    }
  },
  computed: {
    today() {
      return new Date().toISOString().split('T')[0]
    },
    filteredSuppliers() {
      if (!this.searchTerm) {
        return this.suppliers
      }
      const term = this.searchTerm.toLowerCase()
      return this.suppliers.filter(supplier => supplier.supplier_name.toLowerCase().includes(term))
    },
  },
  mounted() {
    this.fetchStats()
  },
  methods: {
    getDefaultStartDate() {
      const date = new Date()
      date.setDate(date.getDate() - 30)
      return date.toISOString().split('T')[0]
    },
    getDefaultEndDate() {
      return new Date().toISOString().split('T')[0]
    },
    async fetchStats() {
      if (!this.filters.startDate || !this.filters.endDate) {
        this.$toast({
          component: 'ToastificationContent',
          props: {
            title: 'Erreur',
            text: 'Veuillez sélectionner les dates de début et de fin',
            variant: 'danger',
            icon: 'AlertCircleIcon',
          },
        })
        return
      }

      try {
        this.isLoading = true
        this.error = null

        const response = await axios.get('/api/payments/delivered-by-supplier/', {
          params: {
            start_date: this.filters.startDate,
            end_date: this.filters.endDate,
          },
        })

        this.suppliers = response.data.suppliers || []
        this.summary = response.data.summary || null

        this.$toast({
          component: 'ToastificationContent',
          props: {
            title: 'Succès',
            text: 'Données chargées avec succès',
            variant: 'success',
            icon: 'CheckIcon',
          },
        })
      } catch (error) {
        console.error('Error fetching supplier stats:', error)
        this.error = error.response?.data?.error || 'Une erreur est survenue lors du chargement des données'

        this.$toast({
          component: 'ToastificationContent',
          props: {
            title: 'Erreur',
            text: this.error,
            variant: 'danger',
            icon: 'AlertCircleIcon',
          },
        })
      } finally {
        this.isLoading = false
      }
    },
    resetFilters() {
      this.filters.startDate = this.getDefaultStartDate()
      this.filters.endDate = this.getDefaultEndDate()
      this.searchTerm = ''
      this.fetchStats()
    },
    formatCurrency(amount) {
      return new Intl.NumberFormat('fr-TN', {
        style: 'currency',
        currency: 'TND',
        minimumFractionDigits: 3,
      }).format(amount || 0)
    },
    getInitials(name) {
      if (!name) return '??'
      const parts = name.split(' ')
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    },
    exportToCSV() {
      const headers = ['Fournisseur', 'Commandes Livré Payé', 'Frais de Livraison', 'Frais Moyen']
      const rows = this.filteredSuppliers.map(supplier => [
        supplier.supplier_name,
        supplier.orders_livre_paye_count,
        supplier.total_delivery_fees.toFixed(3),
        (supplier.orders_livre_paye_count > 0 ? supplier.total_delivery_fees / supplier.orders_livre_paye_count : 0).toFixed(3),
      ])

      let csvContent = 'data:text/csv;charset=utf-8,'
      csvContent += `${headers.join(',')}\n`
      rows.forEach(row => {
        csvContent += `${row.join(',')}\n`
      })

      const encodedUri = encodeURI(csvContent)
      const link = document.createElement('a')
      link.setAttribute('href', encodedUri)
      link.setAttribute('download', `livraisons_par_fournisseur_${this.filters.startDate}_${this.filters.endDate}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      this.$toast({
        component: 'ToastificationContent',
        props: {
          title: 'Succès',
          text: 'Export CSV réussi',
          variant: 'success',
          icon: 'DownloadIcon',
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.supplier-delivery-stats {
  .header-card {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    box-shadow: 0 4px 24px rgba(102, 126, 234, 0.3);
  }

  .stat-card {
    border: none;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
    transition: transform 0.3s ease, box-shadow 0.3s ease;

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    }

    .stat-label {
      font-size: 0.875rem;
      color: #6e6b7b;
      margin-bottom: 0.5rem;
      font-weight: 500;
    }

    .stat-value {
      font-size: 2rem;
      font-weight: 700;
      margin-bottom: 0;
    }

    .stat-icon {
      width: 48px;
      height: 48px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
}
</style>
